import { useEffect, useState } from 'react';
import { Icon } from './Icon';

/**
 * Server-side pagination kontrolü.
 *
 * Zuzzuu `zuzzuu_customers` app'indeki GridTable footer'ı ile bire bir hizalı:
 *
 *   ┌───── sol bölüm ─────┐                       ┌────────── sağ bölüm ──────────┐
 *   Sayfa 1 / 5     Toplam Kayıt: 228         Satırlar: [50 ▼]   « ‹ [1] / 5 › »
 *
 * Buton stili (customers `GridTable.jsx` muiPaginationProps altından):
 *   - 32 × 32 outlined, rounded-lg (#CBCECD border)
 *   - metin #797E88, hover primary-600 border + bg primary/5
 *   - disabled #E5E7EB border + #9CA3AF text
 *
 * Sayfa numarası düzenlenebilir input — klavyeden doğrudan atlayabilmek için.
 *
 * Props:
 *   page          — 1-tabanlı mevcut sayfa
 *   size          — sayfa başına kayıt (50/100/250)
 *   total         — backend'ten gelen toplam kayıt sayısı
 *   onPageChange  — (newPage: number) => void
 *   onSizeChange  — (newSize: number) => void  (opsiyonel)
 *   loading       — true iken butonlar disable
 */
const SIZE_OPTIONS = [50, 100, 250];

// Ortak buton stili — 5 navigasyon butonu için. customers outlined Button
// varyantının Tailwind karşılığı.
const NAV_BTN_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#CBCECD] bg-white text-[#797E88] transition-colors ' +
  'hover:border-primary-600 hover:bg-primary-600/5 ' +
  'disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-400 disabled:hover:bg-white';

export default function Pagination({
  page = 1,
  size = 50,
  total = 0,
  onPageChange,
  onSizeChange,
  loading = false,
  className = '',
}) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / (Number(size) || 50)));
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const [pageInput, setPageInput] = useState(String(safePage));

  // safePage dışarıdan değişirse (size change/goto vb.) input da senkronlansın.
  useEffect(() => {
    setPageInput(String(safePage));
  }, [safePage]);

  const goto = (p) => {
    if (loading) return;
    const np = Math.min(Math.max(1, p), totalPages);
    if (np !== safePage) onPageChange?.(np);
  };

  const handlePageInputChange = (e) => {
    const raw = e.target.value;
    setPageInput(raw);
    if (raw === '') return;
    const num = Number(raw);
    if (Number.isInteger(num) && num >= 1 && num <= totalPages) {
      goto(num);
    }
  };

  const handlePageInputBlur = () => {
    // Geçersiz değerde mevcut sayfaya geri dön.
    const num = Number(pageInput);
    if (!Number.isInteger(num) || num < 1 || num > totalPages) {
      setPageInput(String(safePage));
    }
  };

  return (
    <div className={`flex w-full flex-wrap items-center justify-between gap-3 ${className}`}>
      {/* Sol: Sayfa X / Y  +  Toplam Kayıt: N */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-neutral-600">
          Sayfa <span className="font-medium text-neutral-900">{safePage}</span> / {totalPages}
        </span>
        <span className="font-medium text-neutral-500">
          Toplam Kayıt:{' '}
          <span className="text-neutral-900">
            {(Number(total) || 0).toLocaleString('tr-TR')}
          </span>
        </span>
      </div>

      {/* Sağ: Satırlar select + navigasyon butonları + sayfa input */}
      <div className="flex items-center gap-4">
        {onSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600">Satırlar:</span>
            <select
              value={size}
              onChange={(e) => onSizeChange(Number(e.target.value))}
              disabled={loading}
              className="h-8 cursor-pointer rounded-lg border border-[#CBCECD] bg-white px-2 text-sm text-neutral-900 outline-none transition-colors hover:border-primary-600 focus:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => goto(1)}
              disabled={loading || safePage <= 1}
              className={NAV_BTN_CLASS}
              aria-label="İlk sayfa"
              title="İlk sayfa"
            >
              <Icon name="chevronsLeft" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => goto(safePage - 1)}
              disabled={loading || safePage <= 1}
              className={NAV_BTN_CLASS}
              aria-label="Önceki sayfa"
              title="Önceki sayfa"
            >
              <Icon name="chevronLeft" className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1 px-1">
              <input
                type="number"
                value={pageInput}
                onChange={handlePageInputChange}
                onBlur={handlePageInputBlur}
                min={1}
                max={totalPages}
                disabled={loading}
                className="h-8 w-14 rounded-lg border border-[#CBCECD] px-2 text-center text-sm font-medium text-neutral-900 outline-none transition-colors focus:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <span className="whitespace-nowrap text-xs text-neutral-500">/ {totalPages}</span>
            </div>

            <button
              type="button"
              onClick={() => goto(safePage + 1)}
              disabled={loading || safePage >= totalPages}
              className={NAV_BTN_CLASS}
              aria-label="Sonraki sayfa"
              title="Sonraki sayfa"
            >
              <Icon name="chevronRight" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => goto(totalPages)}
              disabled={loading || safePage >= totalPages}
              className={NAV_BTN_CLASS}
              aria-label="Son sayfa"
              title="Son sayfa"
            >
              <Icon name="chevronsRight" className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
